import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  OffsetPaged,
  ServiceClient,
  ServiceClientContext,
  queryParameterBagFromOffsetPaginationControls,
} from '@droidmap/shared-service-contract'

import {
  AdminProject,
  AuditTrailPage,
  ExchangeSupportSessionCodeResponse,
  ListOrgMembersInput,
  ListOrgProjectsInput,
  ListOrgsInput,
  ListOrgsResponse,
  Org360,
  OrgMembersPage,
  StartSupportSessionInput,
  StartSupportSessionResponse,
  SupportAssistTarget,
  SupportSessionTarget,
  UserLookupResult,
} from '../models/admin-models.js'

const beforeParameter = (before?: string): QueryParameterBag =>
  before === undefined ? {} : { before }

/**
 * Client for the admin service. `getToken` must yield a staff-pool token; the
 * service refuses customer and service tokens on every route.
 *
 * `ServiceClient` revives ISO 8601 instants into `Date`, so timestamp fields
 * typed `string` are `Date` objects at runtime. Do not concatenate or compare
 * them as text: use `auditCursorAfter` for a cursor, `toISOString()` otherwise.
 */
export class AdminServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'AdminService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: AdminServiceClient.SERVICE_NAME })
  }

  /** Every organisation, filtered, sorted and paged. */
  listOrgs = async (
    input: ListOrgsInput = {},
    metadata?: DroidMapMetadata,
  ): Promise<ListOrgsResponse> => {
    const { page, pageSize, sortBy, sortDir, filters } = input
    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        sortBy,
        sortDir,
      })
    if (filters && filters.length > 0) {
      queryParameters['filters'] = JSON.stringify(filters)
    }
    return await this.callApiRouteWithToken<undefined, ListOrgsResponse>({
      method: 'GET',
      path: '/orgs',
      queryParameters,
      metadata,
    })
  }

  /** The composite org read. Rejects with a 404 response for an unknown org. */
  getOrg360 = async (
    input: { orgId: string },
    metadata?: DroidMapMetadata,
  ): Promise<Org360> => {
    return await this.callApiRouteWithToken<undefined, Org360>({
      method: 'GET',
      path: `/orgs/${input.orgId}`,
      metadata,
    })
  }

  /**
   * The org's audit trail, newest first. `before` is `auditCursorAfter` of the
   * oldest entry held; `listActorAudit` and `listCrossOrgAudit` page the same way.
   */
  listOrgAudit = async (
    input: { orgId: string; before?: string },
    metadata?: DroidMapMetadata,
  ): Promise<AuditTrailPage> => {
    return await this.callApiRouteWithToken<undefined, AuditTrailPage>({
      method: 'GET',
      path: `/orgs/${input.orgId}/audit`,
      queryParameters: beforeParameter(input.before),
      metadata,
    })
  }

  /** The org's membership rows, identity joined, paged. */
  listOrgMembers = async (
    input: ListOrgMembersInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgMembersPage> => {
    const { orgId, page, pageSize, filter, sortBy, sortDir, activeOnly } = input
    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      }),
      ...(activeOnly !== undefined ? { activeOnly: String(activeOnly) } : {}),
    }
    return await this.callApiRouteWithToken<undefined, OrgMembersPage>({
      method: 'GET',
      path: `/orgs/${orgId}/members`,
      queryParameters,
      metadata,
    })
  }

  /** The org's projects, paged. */
  listOrgProjects = async (
    input: ListOrgProjectsInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<AdminProject>> => {
    const { orgId, page, pageSize, filter, sortBy, sortDir } = input
    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      })
    return await this.callApiRouteWithToken<
      undefined,
      OffsetPaged<AdminProject>
    >({
      method: 'GET',
      path: `/orgs/${orgId}/projects`,
      queryParameters,
      metadata,
    })
  }

  /** Everything one staff member read, newest first, across every org. */
  listActorAudit = async (
    input: { actorId: string; before?: string },
    metadata?: DroidMapMetadata,
  ): Promise<AuditTrailPage> => {
    return await this.callApiRouteWithToken<undefined, AuditTrailPage>({
      method: 'GET',
      path: '/audit',
      queryParameters: {
        actorId: input.actorId,
        ...beforeParameter(input.before),
      },
      metadata,
    })
  }

  /** The reads that span organisations, by anyone, newest first. */
  listCrossOrgAudit = async (
    input: { before?: string } = {},
    metadata?: DroidMapMetadata,
  ): Promise<AuditTrailPage> => {
    return await this.callApiRouteWithToken<undefined, AuditTrailPage>({
      method: 'GET',
      path: '/audit',
      queryParameters: beforeParameter(input.before),
      metadata,
    })
  }

  /**
   * Exact email match; rejects with a 404 when no user has it. A POST, so the
   * address travels in the body and stays out of request logs.
   */
  findUserByEmail = async (
    input: { email: string },
    metadata?: DroidMapMetadata,
  ): Promise<UserLookupResult> => {
    return await this.callApiRouteWithToken<
      { email: string },
      UserLookupResult
    >({
      method: 'POST',
      path: '/users/lookup',
      body: { email: input.email },
      metadata,
    })
  }

  /** The confirm step for a support session: the user and any support grant. */
  getSupportSessionTarget = async (
    input: { userId: string },
    metadata?: DroidMapMetadata,
  ): Promise<SupportSessionTarget> => {
    return await this.callApiRouteWithToken<undefined, SupportSessionTarget>({
      method: 'GET',
      path: `/support-sessions/targets/${input.userId}`,
      metadata,
    })
  }

  /** The org a read-write session would act in, before one starts. */
  getSupportAssistTarget = async (
    input: { orgId: string },
    metadata?: DroidMapMetadata,
  ): Promise<SupportAssistTarget> => {
    return await this.callApiRouteWithToken<undefined, SupportAssistTarget>({
      method: 'GET',
      path: `/support-sessions/orgs/${input.orgId}`,
      metadata,
    })
  }

  /**
   * Start a support session: read-only (staff in `support-observe`) or
   * read-write (staff in `support-assist`). Either needs a reason.
   */
  startSupportSession = async (
    input: StartSupportSessionInput,
    metadata?: DroidMapMetadata,
  ): Promise<StartSupportSessionResponse> => {
    return await this.callApiRouteWithToken<
      StartSupportSessionInput,
      StartSupportSessionResponse
    >({
      method: 'POST',
      path: '/support-sessions/start',
      body: input,
      metadata,
    })
  }
}

/**
 * The calls the customer web app makes about a support session. They carry
 * no staff token: `exchange` is authorised by its one-time code alone (any
 * bearer is ignored), `end` by the session's own token, which `getToken`
 * must yield once the exchange has answered.
 */
export class SupportSessionClient extends ServiceClient {
  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: AdminServiceClient.SERVICE_NAME })
  }

  /** Redeem the console's one-time code for the session token. */
  exchangeCode = async (
    input: { code: string },
    metadata?: DroidMapMetadata,
  ): Promise<ExchangeSupportSessionCodeResponse> => {
    return await this.callApiRouteWithToken<
      { code: string },
      ExchangeSupportSessionCodeResponse
    >({
      method: 'POST',
      path: '/support-sessions/exchange',
      body: input,
      metadata,
    })
  }

  /**
   * Whether the session is still live. Rejects with a 401 whose
   * `serverMessage` is `support_session_revoked` or `_expired` once it is not.
   */
  getSessionStatus = async (
    metadata?: DroidMapMetadata,
  ): Promise<{ active: true }> => {
    return await this.callApiRouteWithToken<undefined, { active: true }>({
      method: 'GET',
      path: '/support-sessions/current',
      metadata,
    })
  }

  /** End the session; every service refuses its token within 30 seconds. */
  endSession = async (metadata?: DroidMapMetadata): Promise<void> => {
    await this.callApiRouteWithToken<undefined, { ended: true }>({
      method: 'POST',
      path: '/support-sessions/end',
      metadata,
    })
  }
}
